import React, { useState, useRef, useEffect } from "react"
import { Box } from "@material-ui/core"
import { makeStyles } from "@material-ui/styles"
import { Checkbox, MentionOutput, ImagesViewer } from ".."
import { Shine } from "../ActionCards"
import { ModuleHeading } from "../Headings"
import { useMutationCompleteJobTask } from "../../data"
import { useJobUtils } from "../../utils"
import { JobScheduledMessage } from "./JobScheduledMessage"
import { DownloadList } from "../Uploader"
import { SectionTitle } from "../Viewers"
import { RowBox } from "../Boxes"
import { useMutationUpdateJob } from "../../data/jobs/useMutationUpdateJob"
import { JobMoreInformation } from "./JobMoreInformation"

const useStyles = makeStyles((theme) => ({
  scheduledText: {
    color: theme.palette.scheduled.main,
  },
  description: {
    whiteSpace: "pre-wrap",
  },
  detail: {
    backgroundColor: theme.palette.incomplete.background,
    border: `solid 2px ${theme.palette.incomplete.background}`,
    borderRadius: "8px",
  },
  detailOverdue: {
    backgroundColor: theme.palette.warning.background,
    border: `solid 2px ${theme.palette.warning.main}`,
  },
  detailCompleted: {
    backgroundColor: "#f5fffc",
    border: "solid 2px #18ce99",
  },
  detailInner: {
    padding: "20px 24px",
    fontSize: 16,
    lineHeight: "20px",
  },
  detailName: {
    fontSize: 26,
    lineHeight: "36px",
    marginBottom: theme.spacing(1),
  },
  infoLight: {
    color: theme.palette.text.light,
  },
  incomplete: {
    color: theme.palette.primary.main,
    fontWeight: "600",
  },
  success: {
    color: theme.palette.success.main,
    fontWeight: "600",
  },
  completedByAvatar: {
    width: 24,
    height: 24,
  },
}))

const TaskViewer = ({ job, isInline, preview, onDelete }) => {
  const classes = useStyles()
  const [completeJobTask, { loading }] = useMutationCompleteJobTask(job)
  const [updateJob] = useMutationUpdateJob(job)
  const { isJobAfterToday, isJobOverdue } = useJobUtils()
  const initRef = useRef(false)
  const updateRef = useRef(false)
  const {
    id,
    title,
    task: { name, description, uploads },
    availableFrom,
    status: { completed },
  } = job
  const [complete, setComplete] = useState(completed)

  const scheduled = isJobAfterToday(job.availableFrom)
  const overdue = isJobOverdue(job)
  const expansionClasses = {
    root: classes.expansionRoot,
    icon: classes.expansionIcon,
    summary: classes.expansionSummary,
    detail: classes.expansionDetail,
  }

  useEffect(() => {
    if (!initRef.current) {
      setComplete(completed)
      initRef.current = true
    }
  }, [completed])

  useEffect(() => {
    if (updateRef.current) {
      updateRef.current = false
    } else if ((completed && !complete) || (!completed && complete)) {
      setComplete(completed)
    }
  }, [completed, complete])

  const handleCompletedChange = () => {
    updateRef.current = true
    const state = !complete
    setComplete(state)
    completeJobTask({
      variables: {
        id,
        complete: state,
      },
    })
  }

  const handleUpdateTitle = async (newTitle) => {
    if (newTitle === title) {
      return
    }
    await updateJob({ variables: { id: job.id, title: newTitle } })
  }

  const handleDelete = () => {
    onDelete && onDelete()
  }

  return (
    <>
      <Box
        className={`${classes.detail} ${completed ? classes.detailCompleted : ""} ${
          overdue ? classes.detailOverdue : ""
        }`}
      >
        <Shine go={completed}>
          <Box className={classes.detailInner}>
            <ModuleHeading
              id="task-title"
              className={classes.detailName}
              value={title || name}
              editable={!preview && "job_update_all"}
              onEdited={handleUpdateTitle}
              noMargin
            />
            <Box mb={1} className={classes.description}>
              <MentionOutput content={description} />
            </Box>
            {!scheduled && (
              <RowBox ml={-1.5}>
                <Checkbox
                  type="complete"
                  checked={complete}
                  onClick={handleCompletedChange}
                  disabled={loading || scheduled || preview}
                />
                <Box>
                  <strong>Mark task as complete</strong>
                </Box>
              </RowBox>
            )}
            {scheduled && <JobScheduledMessage type="Task" availableFrom={availableFrom} />}
          </Box>
        </Shine>
      </Box>

      <JobMoreInformation
        job={job}
        isInline={isInline}
        classes={classes}
        expansionClasses={expansionClasses}
        onDelete={handleDelete}
        exportPdf={false}
      />

      {uploads?.length > 0 && (
        <Box mb={5}>
          <SectionTitle>Attachments</SectionTitle>
          <ImagesViewer uploads={uploads} />
          <DownloadList uploads={uploads} variant="cards" mt={2} />
        </Box>
      )}
    </>
  )
}

export default TaskViewer
